// UnsavedChangesProvider — single coordination point for generic
// unsaved-changes guards on editable entity windows.
//
// @aiPurpose Aggregate registered editor guards into ONE React Router
//   `useBlocker` and ONE `beforeunload` listener so multiple guarded
//   surfaces never race each other.
// @aiInvariant Exactly one `useBlocker` lives here for generic entity
//   guards. The existing request-draft `DraftDrawer` blocker is intentionally
//   left out of this provider per issue #628 Resolved Decision #6 — that
//   editor and listed entity editors do not coexist on the same screen today.
// @aiInvariant Topmost (most recently registered) guard owns the visible
//   prompt for explicit close paths. Route navigation aggregates across
//   all registered guards because we cannot know in advance which one will
//   be unmounted.
// @aiInvariant Discard handlers set a one-shot bypass so a subsequent
//   programmatic navigation triggered by the discarded close (e.g.
//   `navigate('/users')` from a route-backed drawer) does not re-block.
// @aiInvariant Busy guards (mutation in flight) block route navigation AND
//   `beforeunload` regardless of dirty status. Per #628 Resolved Decision
//   18 a pending owned mutation must not be silently interrupted by
//   navigation or unmount. The route blocker auto-resets (no prompt) while
//   busy — matching the "ignored/disabled" semantics for explicit close.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useBlocker } from 'react-router';
import { UnsavedChangesConfirmModal } from './UnsavedChangesConfirmModal.js';

export interface GuardEntry {
  isDirty: () => boolean;
  isBusy: () => boolean;
}

export interface PromptRequest {
  onDiscard: () => void;
  onContinue?: () => void;
}

interface UnsavedChangesContextValue {
  register: (entry: GuardEntry) => () => void;
  requestPrompt: (req: PromptRequest) => void;
  /**
   * One-shot bypass for the very next blocker callback. Used by editors
   * that synchronously chain `reset() → navigate(...)` inside an
   * `onSuccess` handler: dirty has been cleared at the data layer but
   * the next React render has not committed yet, so the blocker would
   * still see stale dirty=true unless explicitly told to let this hop
   * through. The flag is consumed by exactly one blocker dispatch.
   */
  bypassNext: () => void;
  warningOpen: boolean;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

export function useUnsavedChangesContext(): UnsavedChangesContextValue {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) {
    throw new Error('useUnsavedChangesContext must be used within an <UnsavedChangesProvider>');
  }
  return ctx;
}

export interface UnsavedChangesProviderProps {
  children: ReactNode;
}

export function UnsavedChangesProvider({ children }: UnsavedChangesProviderProps) {
  const idCounter = useRef(0);
  const entries = useRef<Map<number, GuardEntry>>(new Map());
  const orderRef = useRef<number[]>([]);
  const bypassRef = useRef(false);
  const [prompt, setPrompt] = useState<PromptRequest | null>(null);

  const aggregateState = useCallback((): { anyDirty: boolean; anyBusy: boolean } => {
    let anyDirty = false;
    let anyBusy = false;
    for (const e of entries.current.values()) {
      if (e.isBusy()) anyBusy = true;
      if (e.isDirty()) anyDirty = true;
      if (anyDirty && anyBusy) break;
    }
    return { anyDirty, anyBusy };
  }, []);

  // Single useBlocker for all registered generic guards. The provider
  // lives inside `RouterProvider` (mounted from `RootLayout`), so this
  // hook resolves to the active data router's blocker slot. Block when
  // ANY guard is dirty OR busy — busy must not be silently unmounted.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (
      currentLocation.pathname === nextLocation.pathname &&
      currentLocation.search === nextLocation.search
    ) {
      return false;
    }
    if (bypassRef.current) {
      bypassRef.current = false;
      return false;
    }
    const { anyDirty, anyBusy } = aggregateState();
    return anyDirty || anyBusy;
  });

  // When the blocker trips, decide: if any guard is busy, the navigation
  // would interrupt an in-flight mutation — reset it silently with no
  // prompt (the user did not choose to discard). Otherwise open the
  // generic prompt and route the choices into proceed/reset.
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    const { anyBusy } = aggregateState();
    if (anyBusy) {
      blocker.reset?.();
      return;
    }
    setPrompt({
      onDiscard: () => blocker.proceed?.(),
      onContinue: () => blocker.reset?.(),
    });
  }, [blocker, aggregateState]);

  // beforeunload while ANY guard is dirty or busy. Native prompt copy is
  // browser-controlled; we just need a truthy `returnValue`. Including
  // busy here matches the navigation rule above — a hard reload during
  // an in-flight save must not pass silently either.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent): void => {
      const { anyDirty, anyBusy } = aggregateState();
      if (anyDirty || anyBusy) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [aggregateState]);

  const register = useCallback((entry: GuardEntry) => {
    const id = ++idCounter.current;
    entries.current.set(id, entry);
    orderRef.current.push(id);
    return () => {
      entries.current.delete(id);
      orderRef.current = orderRef.current.filter((x) => x !== id);
    };
  }, []);

  const requestPrompt = useCallback((req: PromptRequest) => {
    setPrompt((prev) => prev ?? req);
  }, []);

  const bypassNext = useCallback(() => {
    bypassRef.current = true;
  }, []);

  const handleDiscard = useCallback(() => {
    const current = prompt;
    setPrompt(null);
    bypassRef.current = true;
    try {
      current?.onDiscard();
    } finally {
      // If the discard caused a synchronous navigation, the blocker
      // callback already consumed and cleared `bypassRef`. Otherwise
      // (modal-only close without nav), clear it here so a later
      // unrelated navigation does not silently bypass the guard.
      bypassRef.current = false;
    }
  }, [prompt]);

  const handleContinue = useCallback(() => {
    const current = prompt;
    setPrompt(null);
    current?.onContinue?.();
  }, [prompt]);

  const value = useMemo<UnsavedChangesContextValue>(
    () => ({ register, requestPrompt, bypassNext, warningOpen: prompt !== null }),
    [register, requestPrompt, bypassNext, prompt],
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <UnsavedChangesConfirmModal
        open={prompt !== null}
        onContinue={handleContinue}
        onDiscard={handleDiscard}
      />
    </UnsavedChangesContext.Provider>
  );
}
